import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { IaCFormat } from '@/types/iac';
import { useEditorStore } from '../store';
import { useAuthStore } from '@/features/auth/store';
import { useUpgradeModalStore } from '@/features/billing/upgradeModalStore';

interface IaCMenuProps {
  onChoose: (format: IaCFormat) => void;
}

export function IaCMenu({ onChoose }: IaCMenuProps) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const mode = useEditorStore((s) => s.mode);
  const nodes = useEditorStore((s) => s.nodes);
  const user = useAuthStore((s) => s.user);
  const openUpgrade = useUpgradeModalStore((s) => s.openModal);

  // IaC export is a Pro/BYO feature — creates a concrete upgrade reason.
  // Free users still see the button so they know the feature exists; clicking
  // routes them to the upgrade modal instead of a hidden no-op.
  const isPaid = user?.plan === 'pro' || user?.plan === 'byo';
  const disabled = mode !== 'solution_architecture' || nodes.length === 0;
  const disabledReason =
    mode !== 'solution_architecture'
      ? 'Available in Solution mode only'
      : nodes.length === 0
        ? 'Add nodes first'
        : '';

  useEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setPos({
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (
        buttonRef.current && !buttonRef.current.contains(target) &&
        menuRef.current && !menuRef.current.contains(target)
      ) {
        setOpen(false);
      }
    }
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const choose = (format: IaCFormat) => {
    setOpen(false);
    onChoose(format);
  };

  const handleClick = () => {
    if (disabled) return;
    if (!isPaid) {
      openUpgrade({ defaultPlan: 'pro' });
      return;
    }
    setOpen((s) => !s);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleClick}
        disabled={disabled}
        title={
          disabled
            ? disabledReason
            : isPaid
              ? 'Generate infrastructure code'
              : 'Upgrade to Pro or BYO to export Terraform / CloudFormation'
        }
        className="shrink-0 rounded-clyra border border-brand-500/30 bg-brand-500/10 px-2 py-1 text-micro font-medium text-brand-400 hover:bg-brand-500/20 transition-colors disabled:cursor-not-allowed disabled:opacity-40 disabled:border-line-default disabled:bg-transparent disabled:text-ink-muted"
      >
        Generate IaC {!isPaid && <span className="ml-0.5 text-ink-faint">🔒</span>}
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          className="fixed z-50 w-52 rounded-clyra-lg border border-line-default bg-clyra-elevated/95 backdrop-blur-md py-1.5 shadow-clyra-lg [body.paper_&]:border-stone-default [body.paper_&]:bg-paper-raised"
          style={{ top: pos.top, right: pos.right }}
        >
          <IaCMenuItem
            label="Terraform"
            description="HCL for AWS / GCP / Azure"
            onClick={() => choose('terraform')}
          />
          <IaCMenuItem
            label="CloudFormation"
            description="YAML for AWS"
            onClick={() => choose('cloudformation')}
          />
        </div>,
        document.body,
      )}
    </>
  );
}

function IaCMenuItem({ label, description, onClick }: { label: string; description: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-between px-3 py-2 text-left text-xs text-ink-primary hover:bg-brand-500/10 transition-colors"
    >
      <span className="font-medium">{label}</span>
      <span className="text-micro text-ink-muted">{description}</span>
    </button>
  );
}
